import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2, Store } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetGuestRestaurants, apiGuestSwitchStore } from '@/services/api';
import { getSelectedMerchantId, setSelectedMerchantId, storeRoute } from '@/lib/routes';

function resolveSubPath(pathname = '') {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length < 3) return 'wallet';
  return parts[2] || 'wallet';
}

export default function RestaurantSwitcher({ inline = false, className = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    guestToken,
    guestUser,
    guestLogin,
    setWallet,
    setTransactions,
    setStoreInfo,
    setMerchantInfo,
  } = useStore();

  const [restaurants, setRestaurants] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');

  const phone = useMemo(() => String(guestUser?.phone || '').trim(), [guestUser?.phone]);
  const shouldShow = Boolean(guestToken && phone);
  const selectedMerchantId = getSelectedMerchantId();

  useEffect(() => {
    if (!shouldShow) return;
    let active = true;
    setLoadingList(true);
    setError('');
    apiGetGuestRestaurants(phone)
      .then((items) => {
        if (!active) return;
        setRestaurants(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!active) return;
        setRestaurants([]);
        setError('No restaurants found for this account');
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => {
      active = false;
    };
  }, [phone, shouldShow]);

  useEffect(() => {
    if (!shouldShow || selectedMerchantId) return;
    const fallbackId = String(guestUser?.merchant_id || '').trim();
    if (fallbackId) {
      setSelectedMerchantId(fallbackId);
    }
  }, [guestUser?.merchant_id, selectedMerchantId, shouldShow]);

  if (!shouldShow) return null;

  const handleSwitch = async (event) => {
    const targetMerchantId = String(event.target.value || '').trim();
    const currentSelected = getSelectedMerchantId();
    if (!targetMerchantId || switching || targetMerchantId === currentSelected) return;

    setSwitching(true);
    setError('');
    // Force clean transition: selected_merchant_id is the single source of truth.
    window.localStorage.removeItem('selected_merchant_id');
    setSelectedMerchantId(targetMerchantId);
    try {
      // Clear old restaurant data first to avoid stale flashes.
      setWallet(null);
      setTransactions([]);
      setStoreInfo(null);
      setMerchantInfo(null);

      const result = await apiGuestSwitchStore(targetMerchantId);
      guestLogin(result.token, result.customer, result.merchant);
      setSelectedMerchantId(targetMerchantId);

      const subPath = resolveSubPath(location.pathname);
      navigate(storeRoute(targetMerchantId, subPath), { replace: true });
    } catch (e) {
      // Restore previous merchant context if switch failed.
      setSelectedMerchantId(currentSelected);
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to switch restaurant right now.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div
      className={`${inline ? '' : 'fixed left-4 top-4 z-[75] w-[230px]'} ${className}`}
      data-testid="guest-restaurant-switcher"
    >
      <label className="text-[10px] font-bold uppercase tracking-wider text-brand">Select Restaurant</label>
      <div className="mt-1 relative">
        <Store className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-brand" />
        <select
          value={selectedMerchantId || ''}
          onChange={handleSwitch}
          disabled={loadingList || switching}
          className="h-10 w-full rounded-xl border border-brand/30 bg-card/95 pl-8 pr-8 text-xs font-semibold text-foreground shadow-sm backdrop-blur-sm appearance-none disabled:opacity-70"
          data-testid="guest-restaurant-select"
        >
          {!restaurants.length && (
            <option value="">
              {loadingList ? 'Loading...' : 'No restaurants found for this account'}
            </option>
          )}
          {restaurants.map((restaurant) => (
            <option key={restaurant.id} value={restaurant.id}>
              {restaurant.location
                ? `${restaurant.restaurant_name} • ${restaurant.location}`
                : restaurant.restaurant_name}
            </option>
          ))}
        </select>
        {switching || loadingList ? (
          <Loader2 className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-brand" />
        ) : (
          <ChevronDown className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-brand" />
        )}
      </div>
      {error && (
        <p className="mt-1 text-[11px] text-destructive font-medium" data-testid="guest-restaurant-switch-error">
          {error}
        </p>
      )}
    </div>
  );
}
